import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarMonth,
  CheckCircle,
  Close,
  EventBusy,
  ErrorOutline,
  EventRepeat,
  ViewList,
  VisibilityOff,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';
import PackBookingsCancelPane from './PackBookingsCancelPane';

/**
 * Bulk-book one member onto their recurring classes, paid from one pack.
 *
 *   1. CLASS → DAYS → TIME — the way staff say it: "Sculpt, Mon/Wed/Fri,
 *      6:30". Only days and times the class actually runs are offered; a member
 *      who comes at different times on different days can set a time per day.
 *   2. HOW LONG — a number of classes, until a date (+2 weeks / +1 month /
 *      +3 months), or until the pack ends. A 3–6 month pack usually outlives the
 *      studio's published timetable, so the panel says how far sessions exist
 *      and books those; the rest can be booked later with the same pattern.
 *   3. PREVIEW — a month calendar (or list) of every date: will book, already
 *      booked, or why not (full, frozen, out of credit…). Click a date to skip
 *      it (holiday, travel).
 *
 * Booking runs through the normal manual-booking path one class at a time; a
 * date that fails at that moment is reported and the rest still book.
 */

interface Slot { weekday: number; time: string; upcoming: number; firstDate: string; lastDate?: string }
interface ClassOption { classId: string; className: string; sessionType: string; slots: Slot[]; lastDate?: string }
interface PackSummary {
  purchasedPackId: string;
  title: string;
  member: { _id: string; name: string; phoneNumber: string };
  totalClasses: number | null;
  classesLeft: number | null;
  unlimited: boolean;
  startDate: string | null;
  expiryDate: string | null;
  sessionType: string | null;
  isCombo: boolean;
  isFrozen: boolean;
}
type RowStatus =
  | 'bookable' | 'already_booked' | 'full' | 'before_start' | 'after_expiry'
  | 'frozen' | 'type_mismatch' | 'not_covered' | 'no_credit';
interface PreviewRow {
  sessionId: string;
  day: string;
  weekday: number;
  time: string;
  className: string;
  slots: number;
  booked: number;
  hidden: boolean;
  status: RowStatus;
  reason: string | null;
}
interface Preview {
  rows: PreviewRow[];
  selectable: number;
  creditsAfter: number | null;
  scheduleEndsOn: string | null;
  packEndsOn: string | null;
  stoppedBy: 'count' | 'max' | 'expiry' | 'credits' | 'schedule';
}
interface Result {
  booked: { sessionId: string; day: string; time: string; className: string; bookingId: string | null }[];
  failed: { sessionId: string; day?: string; time?: string; reason: string }[];
  classesLeft: number | null;
}
type RangeMode = 'count' | 'until' | 'packEnd';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after at least one booking was made, so the page can refresh. */
  onBooked: () => void;
  purchasedPackId: string | null;
  /** The pack's studio — required for platform admins, ignored for a studio login. */
  studioId?: string;
}

// ── dates (all IST calendar days as YYYY-MM-DD) ────────────────────────────
const IST_MS = 5.5 * 3600e3;
const DAYS_MON_FIRST = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const todayIst = () => new Date(Date.now() + IST_MS).toISOString().slice(0, 10);
const istKey = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() + IST_MS).toISOString().slice(0, 10) : null);
const addDays = (key: string, n: number) => new Date(Date.parse(`${key}T00:00:00Z`) + n * 864e5).toISOString().slice(0, 10);
const addMonths = (key: string, n: number) => {
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
};
const minKey = (a: string, b: string | null) => (b && b < a ? b : a);
const fmtDay = (key: string) =>
  new Date(`${key}T00:00:00Z`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtDate = (key: string) =>
  new Date(`${key}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const fmtMonth = (ym: string) =>
  new Date(`${ym}-01T00:00:00Z`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const fmtTime = (hm: string) => {
  const [h, m] = hm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
};
const errMessage = (e: any, fallback: string) => {
  const m = e?.message ?? e?.data?.message;
  return Array.isArray(m) ? m.join(', ') : m || fallback;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;


// Preview chip colours by status.
const STATUS_STYLE: Record<string, { bg: string; fg: string; border: string }> = {
  book: { bg: 'primary.main', fg: 'primary.contrastText', border: 'primary.main' },
  skip: { bg: 'transparent', fg: 'text.disabled', border: 'divider' },
  booked: { bg: '#E3F2FD', fg: 'info.dark', border: 'info.light' },
  blocked: { bg: 'grey.100', fg: 'text.secondary', border: 'grey.300' },
};

const BulkPackBookingDialog: React.FC<Props> = ({ open, onClose, onBooked, purchasedPackId, studioId }) => {
  // ── loaded pack + what it can book ────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pack, setPack] = useState<PackSummary | null>(null);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [scheduleEndsOn, setScheduleEndsOn] = useState<string | null>(null);
  const [packEndsOn, setPackEndsOn] = useState<string | null>(null);

  // ── pattern + range ───────────────────────────────────────────────────────
  const [classId, setClassId] = useState('');
  const [days, setDays] = useState<number[]>([]);
  // One time for every chosen day, or (perDay) a time per day.
  const [time, setTime] = useState<string | null>(null);
  const [perDay, setPerDay] = useState(false);
  const [dayTimes, setDayTimes] = useState<Record<number, string>>({});
  const [mode, setMode] = useState<RangeMode>('count');
  const [startDate, setStartDate] = useState(todayIst());
  const [count, setCount] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notify, setNotify] = useState(true);

  // ── preview ───────────────────────────────────────────────────────────────
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [view, setView] = useState<'calendar' | 'list'>('calendar');

  // ── submit ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  // Book onto the pack, or cancel bookings already made with it.
  const [tab, setTab] = useState<'book' | 'cancel'>('book');
  // Bumped after a cancel so the Book side re-reads the pack's credits.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (open) setTab('book');
  }, [open, purchasedPackId]);

  useEffect(() => {
    if (!open || !purchasedPackId) return;
    setLoading(true);
    setLoadError(null);
    setPack(null);
    setClasses([]);
    setPreview(null);
    setPreviewError(null);
    setUnticked(new Set());
    setResult(null);
    setSubmitError(null);
    setNotify(true);
    setView('calendar');
    apiService
      .getBulkPackBookingOptions(purchasedPackId, studioId)
      .then((res: any) => {
        const p: PackSummary = res.pack;
        const list: ClassOption[] = res.classes ?? [];
        setPack(p);
        setClasses(list);
        setScheduleEndsOn(res.scheduleEndsOn ?? null);
        setPackEndsOn(res.packEndsOn ?? istKey(p.expiryDate));
        setClassId(list.length === 1 ? list[0].classId : '');
        const packStart = istKey(p.startDate);
        const start = packStart && packStart > todayIst() ? packStart : todayIst();
        setStartDate(start);
        setMode(p.unlimited ? 'until' : 'count');
        setCount(p.unlimited ? '' : String(Math.min(200, Math.max(0, p.classesLeft ?? 0))));
        setEndDate(minKey(addMonths(start, 1), istKey(p.expiryDate)));
      })
      .catch((e: any) => setLoadError(errMessage(e, 'Could not load this pack.')))
      .finally(() => setLoading(false));
  }, [open, purchasedPackId, studioId, reloadKey]);

  const classById = useMemo(() => new Map(classes.map((c) => [c.classId, c])), [classes]);

  const cls = classById.get(classId) ?? null;
  // weekday → times the class runs that day (sorted).
  const timesByDay = useMemo(() => {
    const m = new Map<number, string[]>();
    for (const sl of cls?.slots ?? []) {
      if (!m.has(sl.weekday)) m.set(sl.weekday, []);
      m.get(sl.weekday)!.push(sl.time);
    }
    m.forEach((v) => v.sort());
    return m;
  }, [cls]);
  const runs = (d: number, t: string | null | undefined) => !!t && (timesByDay.get(d) ?? []).includes(t);
  const allTimes = useMemo(
    () => Array.from(new Set((cls?.slots ?? []).map((sl) => sl.time))).sort(),
    [cls],
  );
  // Times worth offering: those running on at least one chosen day.
  const timeOptions = days.length ? allTimes.filter((t) => days.some((d) => runs(d, t))) : allTimes;
  const timeFor = (d: number) => (perDay ? dayTimes[d] : time);
  const missingDays = !perDay && time ? days.filter((d) => !runs(d, time)) : [];

  // A new class starts from scratch; a class with one time picks it.
  useEffect(() => {
    setDays([]);
    setPerDay(false);
    setDayTimes({});
    setTime(allTimes.length === 1 ? allTimes[0] : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  // ── preview request ───────────────────────────────────────────────────────
  const countNum = Number(count);
  const countValid = mode !== 'count' || (Number.isInteger(countNum) && countNum >= 1 && countNum <= 200);
  const endValid = mode !== 'until' || (!!endDate && endDate >= startDate);
  const slotsPayload = useMemo(
    () =>
      days
        .filter((d) => runs(d, timeFor(d)))
        .map((d) => ({ classId, weekday: d, time: timeFor(d) as string })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, time, perDay, dayTimes, classId, timesByDay],
  );
  const canPreview = !!pack && slotsPayload.length > 0 && !!startDate && countValid && endValid && !result;
  const seq = useRef(0);
  useEffect(() => {
    if (!canPreview || !purchasedPackId) { setPreview(null); setPreviewError(null); return; }
    const mine = ++seq.current;
    setPreviewing(true);
    setPreviewError(null);
    const t = setTimeout(() => {
      apiService
        .previewBulkPackBooking({
          purchasedPackId,
          classId,
          slots: slotsPayload,
          startDate,
          ...(mode === 'count' ? { count: countNum } : {}),
          ...(mode === 'until' ? { endDate } : {}),
          studioId,
        })
        .then((res: any) => {
          if (mine !== seq.current) return;
          setPreview(res);
          setUnticked(new Set());
        })
        .catch((e: any) => { if (mine === seq.current) { setPreview(null); setPreviewError(errMessage(e, 'Could not preview these dates.')); } })
        .finally(() => { if (mine === seq.current) setPreviewing(false); });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPreview, purchasedPackId, slotsPayload, startDate, mode, countNum, endDate, studioId]);

  const rows = useMemo(() => preview?.rows ?? [], [preview]);
  const bookable = rows.filter((r) => r.status === 'bookable');
  const toBook = bookable.filter((r) => !unticked.has(r.sessionId));
  const creditsAfter = pack && !pack.unlimited && pack.classesLeft != null ? pack.classesLeft - toBook.length : null;
  const multiClass = new Set(rows.map((r) => r.className)).size > 1;

  const byMonth = useMemo(() => {
    const m = new Map<string, PreviewRow[]>();
    for (const r of rows) {
      const k = r.day.slice(0, 7);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    return m;
  }, [rows]);

  // ── actions ───────────────────────────────────────────────────────────────
  const toggleDay = (d: number) =>
    setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))));
  const setDayPreset = (preset: number[]) => setDays(preset.filter((d) => timesByDay.has(d)));
  const switchToPerDayTimes = () => {
    // Keep the common time where it runs; elsewhere start from that day's first time.
    const next: Record<number, string> = {};
    days.forEach((d) => { next[d] = runs(d, time) ? (time as string) : (timesByDay.get(d) ?? [])[0]; });
    setDayTimes(next);
    setPerDay(true);
  };
  const toggleRow = (id: string) =>
    setUnticked((u) => { const n = new Set(u); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const setMonthTicked = (month: string, on: boolean) =>
    setUnticked((u) => {
      const n = new Set(u);
      for (const r of byMonth.get(month) ?? []) if (r.status === 'bookable') { if (on) n.delete(r.sessionId); else n.add(r.sessionId); }
      return n;
    });

  const submit = async () => {
    if (!purchasedPackId || !toBook.length) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res: any = await apiService.createBulkPackBooking({
        purchasedPackId,
        sessionIds: toBook.map((r) => r.sessionId),
        notifyMember: notify,
        studioId,
      });
      setResult(res);
      if (res?.booked?.length) onBooked();
    } catch (e: any) {
      setSubmitError(errMessage(e, 'Booking failed. Nothing was booked.'));
    } finally {
      setSubmitting(false);
    }
  };
  const close = () => { if (!submitting) onClose(); };

  // ── pieces ────────────────────────────────────────────────────────────────
  const scheduleShort = !!scheduleEndsOn && !!packEndsOn && scheduleEndsOn < packEndsOn;
  const stopNote = (() => {
    if (!preview) return null;
    switch (preview.stoppedBy) {
      case 'credits': return 'Stops where the pack runs out of credits.';
      case 'expiry': return `Stops at the pack's expiry (${fmtDate(preview.packEndsOn!)}).`;
      case 'max': return 'At most 200 classes per run — run it again for the rest.';
      case 'schedule':
        return preview.scheduleEndsOn
          ? `No more of these sessions on the timetable after ${fmtDate(preview.scheduleEndsOn)}.`
          : 'No matching sessions on the timetable in this range.';
      default: return null;
    }
  })();

  const chipFor = (r: PreviewRow) => {
    const can = r.status === 'bookable';
    const style = can
      ? unticked.has(r.sessionId) ? STATUS_STYLE.skip : STATUS_STYLE.book
      : r.status === 'already_booked' ? STATUS_STYLE.booked : STATUS_STYLE.blocked;
    const label = `${fmtTime(r.time)}${multiClass ? ` ${r.className}` : ''}`;
    const tip = can
      ? `${r.className} · ${r.booked}/${r.slots} booked${unticked.has(r.sessionId) ? ' · skipped (click to book)' : ' · click to skip'}`
      : `${r.className} · ${r.reason}`;
    return (
      <Tooltip key={r.sessionId} title={tip} arrow>
        <ButtonBase
          onClick={can ? () => toggleRow(r.sessionId) : undefined}
          disabled={!can}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            px: 0.5,
            py: '1px',
            borderRadius: 0.75,
            fontSize: 10.5,
            lineHeight: 1.4,
            fontWeight: 600,
            border: '1px solid',
            borderColor: style.border,
            bgcolor: style.bg,
            color: style.fg,
            textDecoration: can && unticked.has(r.sessionId) ? 'line-through' : 'none',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            display: 'block',
            textAlign: 'left',
          }}
        >
          {r.status === 'already_booked' ? '✓ ' : ''}{label}
        </ButtonBase>
      </Tooltip>
    );
  };

  // A plain render function, not a component: defined inside the dialog, a
  // component would remount every render and drop tooltip/hover state.
  const renderMonth = (month: string) => {
    const list = byMonth.get(month) ?? [];
    const byDay = new Map<string, PreviewRow[]>();
    list.forEach((r) => { if (!byDay.has(r.day)) byDay.set(r.day, []); byDay.get(r.day)!.push(r); });
    const first = `${month}-01`;
    const lead = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7; // Mon-first
    const daysIn = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();
    const cells: (string | null)[] = [...Array(lead).fill(null), ...Array.from({ length: daysIn }, (_, i) => addDays(first, i))];
    while (cells.length % 7) cells.push(null);
    const monthBook = list.filter((r) => r.status === 'bookable' && !unticked.has(r.sessionId)).length;
    const monthBookable = list.filter((r) => r.status === 'bookable').length;
    const today = todayIst();
    return (
      <Box key={month} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1} bgcolor="grey.50">
          <Typography variant="body2" fontWeight={700}>{fmtMonth(month)}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">{plural(monthBook, 'class', 'classes')}</Typography>
            {monthBookable ? (
              <Button size="small" sx={{ minWidth: 0, py: 0, fontSize: 11 }} onClick={() => setMonthTicked(month, monthBook < monthBookable)}>
                {monthBook < monthBookable ? 'Book all' : 'Skip month'}
              </Button>
            ) : null}
          </Box>
        </Box>
        <Box display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))">
          {DAYS_MON_FIRST.map((w) => (
            <Box key={w} textAlign="center" py={0.5} sx={{ fontSize: 11, color: 'text.secondary', borderTop: '1px solid', borderColor: 'divider' }}>
              {WEEKDAY[w]}
            </Box>
          ))}
          {cells.map((d, i) => {
            const items = d ? byDay.get(d) ?? [] : [];
            return (
              <Box
                key={d ?? `x${i}`}
                sx={{
                  minHeight: 58,
                  p: 0.5,
                  borderTop: '1px solid',
                  borderLeft: i % 7 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  bgcolor: d ? 'background.paper' : 'grey.50',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                }}
              >
                {d ? (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 11, lineHeight: 1.2, fontWeight: d === today ? 700 : 400, color: d === today ? 'primary.main' : items.length ? 'text.primary' : 'text.disabled' }}
                  >
                    {Number(d.slice(8))}
                  </Typography>
                ) : null}
                {items.map(chipFor)}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={close} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <EventRepeat color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600}>{tab === 'book' ? 'Bulk book classes' : 'Cancel pack bookings'}</Typography>
              {pack ? (
                <Typography variant="body2" color="text.secondary">
                  {pack.member.name || 'Member'} · {pack.member.phoneNumber} · {pack.title}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {(pack || tab === 'cancel') && !result ? (
              <ToggleButtonGroup size="small" exclusive value={tab} onChange={(_, v) => v && setTab(v)}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}>
                <ToggleButton value="book"><EventRepeat fontSize="small" sx={{ mr: 0.5 }} />Book</ToggleButton>
                <ToggleButton value="cancel"><EventBusy fontSize="small" sx={{ mr: 0.5 }} />Cancel bookings</ToggleButton>
              </ToggleButtonGroup>
            ) : null}
            <IconButton size="small" onClick={close} disabled={submitting}><Close fontSize="small" /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Not gated on `pack`: the post-cancel reload clears it for a moment,
            and unmounting here would drop the "Cancelled N" confirmation. */}
        {purchasedPackId && tab === 'cancel' ? (
          <PackBookingsCancelPane
            purchasedPackId={purchasedPackId}
            studioId={studioId}
            onCancelled={() => { onBooked(); setReloadKey((k) => k + 1); }}
          />
        ) : loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : !pack ? null : result ? (
          // ── result ──
          <Box display="flex" flexDirection="column" gap={2}>
            {result.booked.length ? (
              <Alert severity="success" icon={<CheckCircle />}>
                Booked {plural(result.booked.length, 'class', 'classes')} for {pack.member.name || 'the member'}.
                {result.classesLeft != null ? ` ${plural(result.classesLeft, 'credit')} left on the pack.` : ''}
              </Alert>
            ) : null}
            {result.failed.length ? (
              <Alert severity="warning" icon={<ErrorOutline />}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  {plural(result.failed.length, 'date')} could not be booked
                </Typography>
                {result.failed.map((f) => (
                  <Typography key={f.sessionId} variant="body2">
                    {f.day ? `${fmtDay(f.day)} ${f.time ? fmtTime(f.time) : ''}` : f.sessionId} — {f.reason}
                  </Typography>
                ))}
              </Alert>
            ) : null}
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Booking ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.booked.map((b) => (
                    <TableRow key={b.sessionId}>
                      <TableCell>{fmtDay(b.day)}</TableCell>
                      <TableCell>{fmtTime(b.time)}</TableCell>
                      <TableCell>{b.className}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{b.bookingId ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3}>
            {/* ── pack facts ── */}
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <Chip size="small" color="primary" variant="outlined"
                label={pack.unlimited ? 'Unlimited credits' : `${pack.classesLeft ?? 0} of ${pack.totalClasses ?? '—'} credits left`} />
              <Chip size="small" variant="outlined"
                label={`Valid ${istKey(pack.startDate) ? fmtDate(istKey(pack.startDate)!) : '—'} – ${packEndsOn ? fmtDate(packEndsOn) : 'no expiry'}`} />
              {scheduleEndsOn ? <Chip size="small" variant="outlined" label={`Timetable published until ${fmtDate(scheduleEndsOn)}`} /> : null}
              {pack.sessionType ? <Chip size="small" variant="outlined" label={`${pack.sessionType} pack`} sx={{ textTransform: 'capitalize' }} /> : null}
              {pack.isCombo ? <Chip size="small" variant="outlined" label="Combo pack" /> : null}
              {pack.isFrozen ? <Chip size="small" color="info" variant="outlined" label="Frozen" /> : null}
            </Box>

            {!pack.unlimited && (pack.classesLeft ?? 0) <= 0 ? (
              <Alert severity="warning">This pack has no credits left to book with.</Alert>
            ) : classes.length === 0 ? (
              <Alert severity="info">There are no upcoming classes this pack can pay for before it expires.</Alert>
            ) : (
              <>
                {scheduleShort ? (
                  <Alert severity="info" sx={{ py: 0.25 }}>
                    This pack runs until <b>{fmtDate(packEndsOn!)}</b>, but the studio's timetable is only published until{' '}
                    <b>{fmtDate(scheduleEndsOn!)}</b>. Only sessions already on the timetable can be booked now — open this
                    again later with the same pattern to book the rest.
                  </Alert>
                ) : null}

                {/* ── 1. class ── */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>1 · Class</Typography>
                  {classes.length === 1 ? (
                    <Typography variant="body1" fontWeight={600}>{classes[0].className}</Typography>
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 280, maxWidth: '100%' }}>
                      <InputLabel>Choose a class</InputLabel>
                      <Select label="Choose a class" value={classId} onChange={(e) => setClassId(e.target.value)}>
                        {classes.map((c) => (
                          <MenuItem key={c.classId} value={c.classId}>{c.className}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {cls ? (
                  <>
                    {/* ── 2. days ── */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} mb={1}>2 · Which days does the member come?</Typography>
                      <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                        {DAYS_MON_FIRST.map((d) => {
                          const available = timesByDay.has(d);
                          const on = days.includes(d);
                          const pill = (
                            <ButtonBase
                              key={d}
                              onClick={available ? () => toggleDay(d) : undefined}
                              disabled={!available}
                              aria-pressed={on}
                              sx={{
                                width: 64,
                                height: 40,
                                borderRadius: 5,
                                border: '1.5px solid',
                                borderColor: on ? 'primary.main' : 'grey.300',
                                bgcolor: on ? 'primary.main' : 'background.paper',
                                color: on ? 'primary.contrastText' : available ? 'text.primary' : 'text.disabled',
                                fontWeight: 600,
                                fontSize: 14,
                                opacity: available ? 1 : 0.45,
                                '&:hover': available ? { borderColor: 'primary.main' } : undefined,
                              }}
                            >
                              {WEEKDAY[d]}
                            </ButtonBase>
                          );
                          return available ? pill : (
                            <Tooltip key={d} title={`${cls.className} doesn't run on ${WEEKDAY[d]}`} arrow><span>{pill}</span></Tooltip>
                          );
                        })}
                      </Box>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {[
                          { label: 'Mon · Wed · Fri', d: [1, 3, 5] },
                          { label: 'Tue · Thu · Sat', d: [2, 4, 6] },
                          { label: 'Weekdays', d: [1, 2, 3, 4, 5] },
                          { label: 'Every day', d: [1, 2, 3, 4, 5, 6, 0] },
                        ].map((p) => (
                          <Button key={p.label} size="small" sx={{ textTransform: 'none' }} onClick={() => setDayPreset(p.d)}>{p.label}</Button>
                        ))}
                        {days.length ? (
                          <Button size="small" color="inherit" sx={{ textTransform: 'none', color: 'text.secondary' }} onClick={() => setDays([])}>Clear</Button>
                        ) : null}
                      </Box>
                    </Box>

                    {/* ── 3. time ── */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} mb={1}>3 · At what time?</Typography>
                      {!perDay ? (
                        <>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {timeOptions.map((t) => {
                              const on = time === t;
                              const where = DAYS_MON_FIRST.filter((d) => runs(d, t)).map((d) => WEEKDAY[d]).join(', ');
                              return (
                                <Tooltip key={t} title={`Runs on ${where}`} arrow>
                                  <Chip
                                    clickable
                                    onClick={() => setTime(on ? null : t)}
                                    color={on ? 'primary' : 'default'}
                                    variant={on ? 'filled' : 'outlined'}
                                    label={fmtTime(t)}
                                    sx={{ fontWeight: 600, height: 36, borderRadius: 5, px: 0.5 }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                          {missingDays.length ? (
                            <Alert severity="warning" sx={{ mt: 1.5, py: 0.25 }}
                              action={<Button size="small" color="inherit" sx={{ textTransform: 'none' }} onClick={switchToPerDayTimes}>Set a time per day</Button>}>
                              There is no {fmtTime(time!)} class on {missingDays.map((d) => WEEKDAY[d]).join(', ')} — {missingDays.length === 1 ? 'that day' : 'those days'} won't be booked.
                            </Alert>
                          ) : days.length > 1 ? (
                            <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={switchToPerDayTimes}>
                              Different time on different days?
                            </Button>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {days.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">Choose the days first.</Typography>
                          ) : (
                            <Box display="flex" flexDirection="column" gap={1}>
                              {days.map((d) => (
                                <Box key={d} display="flex" alignItems="center" gap={1.5}>
                                  <Typography variant="body2" fontWeight={600} sx={{ width: 40 }}>{WEEKDAY[d]}</Typography>
                                  <Box display="flex" flexWrap="wrap" gap={0.75}>
                                    {(timesByDay.get(d) ?? []).map((t) => {
                                      const on = dayTimes[d] === t;
                                      return (
                                        <Chip key={t} size="small" clickable
                                          onClick={() => setDayTimes((m) => ({ ...m, [d]: t }))}
                                          color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                                          label={fmtTime(t)} sx={{ fontWeight: 600 }} />
                                      );
                                    })}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                          <Button size="small" sx={{ mt: 1, textTransform: 'none' }}
                            onClick={() => { setPerDay(false); setTime(dayTimes[days[0]] ?? null); }}>
                            Use one time for all days
                          </Button>
                        </>
                      )}
                    </Box>
                  </>
                ) : null}

                {/* ── 2. how long ── */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>4 · For how long?</Typography>
                  <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}
                    sx={{ mb: 1.5, flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75 } }}>
                    {!pack.unlimited ? <ToggleButton value="count">Number of classes</ToggleButton> : null}
                    <ToggleButton value="until">Until a date</ToggleButton>
                    <ToggleButton value="packEnd">Until the pack ends</ToggleButton>
                  </ToggleButtonGroup>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                      <TextField
                        fullWidth size="small" type="date" label="Start from" value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayIst(), max: packEndsOn ?? undefined } }}
                      />
                    </Grid>
                    {mode === 'count' ? (
                      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                        <TextField
                          fullWidth size="small" type="number" label="Number of classes" value={count}
                          onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                          onWheel={stopWheelNudge}
                          error={count !== '' && !countValid}
                          helperText={count !== '' && !countValid ? '1 to 200' : pack.unlimited ? undefined : `Pack has ${pack.classesLeft ?? 0} credits`}
                          slotProps={{ htmlInput: { min: 1, max: 200 } }}
                        />
                      </Grid>
                    ) : null}
                    {mode === 'until' ? (
                      <>
                        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                          <TextField
                            fullWidth size="small" type="date" label="Until (inclusive)" value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            error={!!endDate && !endValid}
                            helperText={!!endDate && !endValid ? 'Must be on or after the start' : undefined}
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: startDate, max: packEndsOn ?? undefined } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box display="flex" gap={0.75} flexWrap="wrap" pt={0.5}>
                            {[
                              { label: '2 weeks', to: () => addDays(startDate, 13) },
                              { label: '1 month', to: () => addDays(addMonths(startDate, 1), -1) },
                              { label: '3 months', to: () => addDays(addMonths(startDate, 3), -1) },
                              { label: '6 months', to: () => addDays(addMonths(startDate, 6), -1) },
                            ].map((q) => {
                              const target = minKey(q.to(), packEndsOn);
                              return (
                                <Chip key={q.label} size="small" clickable variant={endDate === target ? 'filled' : 'outlined'}
                                  color={endDate === target ? 'primary' : 'default'} label={`+${q.label}`} onClick={() => setEndDate(target)} />
                              );
                            })}
                          </Box>
                        </Grid>
                      </>
                    ) : null}
                    {mode === 'packEnd' ? (
                      <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                        <Typography variant="body2" color="text.secondary" pt={1}>
                          Every matching session {packEndsOn ? `until ${fmtDate(packEndsOn)}` : 'on the timetable'}
                          {pack.unlimited ? '' : `, as far as the ${pack.classesLeft ?? 0} credits go`}.
                        </Typography>
                      </Grid>
                    ) : null}
                  </Grid>
                </Box>

                {/* ── 3. preview ── */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight={700}>5 · Dates</Typography>
                      {previewing ? <CircularProgress size={14} /> : null}
                    </Box>
                    {rows.length ? (
                      <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
                        <ToggleButton value="calendar" aria-label="Calendar"><CalendarMonth fontSize="small" /></ToggleButton>
                        <ToggleButton value="list" aria-label="List"><ViewList fontSize="small" /></ToggleButton>
                      </ToggleButtonGroup>
                    ) : null}
                  </Box>

                  {previewError ? <Alert severity="error">{previewError}</Alert> : null}
                  {!canPreview ? (
                    <Typography variant="body2" color="text.secondary">
                      {!cls
                        ? 'Choose a class to begin.'
                        : days.length === 0
                          ? 'Choose the days the member comes.'
                          : slotsPayload.length === 0
                            ? 'Choose a time.'
                            : 'Check the dates above.'}
                    </Typography>
                  ) : !rows.length && !previewing ? (
                    <Typography variant="body2" color="text.secondary">No sessions on the timetable for this pattern and range.</Typography>
                  ) : rows.length ? (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {/* summary */}
                      <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={700}>{plural(toBook.length, 'class', 'classes')} to book</Typography>
                        {Array.from(byMonth.keys()).map((m) => {
                          const n = (byMonth.get(m) ?? []).filter((r) => r.status === 'bookable' && !unticked.has(r.sessionId)).length;
                          return n ? <Typography key={m} variant="body2" color="text.secondary">{fmtMonth(m).split(' ')[0]}: {n}</Typography> : null;
                        })}
                        {creditsAfter != null ? (
                          <Typography variant="body2" color="text.secondary">· {plural(creditsAfter, 'credit')} left after</Typography>
                        ) : null}
                        {rows.length - bookable.length ? (
                          <Typography variant="body2" color="text.secondary">· {plural(rows.length - bookable.length, 'date')} not bookable</Typography>
                        ) : null}
                      </Box>
                      {stopNote ? <Typography variant="caption" color="text.secondary">{stopNote}</Typography> : null}

                      {view === 'calendar' ? (
                        <>
                          <Box display="flex" gap={1.5} flexWrap="wrap" sx={{ fontSize: 11, color: 'text.secondary' }}>
                            {[
                              { s: STATUS_STYLE.book, t: 'Will book (click to skip)' },
                              { s: STATUS_STYLE.skip, t: 'Skipped' },
                              { s: STATUS_STYLE.booked, t: 'Already booked' },
                              { s: STATUS_STYLE.blocked, t: 'Can’t book (hover for why)' },
                            ].map((l) => (
                              <Box key={l.t} display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, border: '1px solid', borderColor: l.s.border, bgcolor: l.s.bg }} />
                                {l.t}
                              </Box>
                            ))}
                          </Box>
                          <Grid container spacing={1.5}>
                            {Array.from(byMonth.keys()).map((m) => (
                              <Grid key={m} size={{ xs: 12, md: byMonth.size > 1 ? 6 : 12 }}>
                                {renderMonth(m)}
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      ) : (
                        <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Date</TableCell>
                                <TableCell>Time</TableCell>
                                {multiClass ? <TableCell>Class</TableCell> : null}
                                <TableCell>Booked</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Array.from(byMonth.entries()).map(([m, list]) => (
                                <React.Fragment key={m}>
                                  <TableRow>
                                    <TableCell colSpan={multiClass ? 6 : 5} sx={{ bgcolor: 'grey.50', fontWeight: 700, py: 0.75 }}>
                                      {fmtMonth(m)}
                                    </TableCell>
                                  </TableRow>
                                  {list.map((r) => {
                                    const can = r.status === 'bookable';
                                    return (
                                      <TableRow key={r.sessionId} sx={{ opacity: can ? 1 : 0.6 }}>
                                        <TableCell padding="checkbox">
                                          <Checkbox size="small" disabled={!can} checked={can && !unticked.has(r.sessionId)} onChange={() => toggleRow(r.sessionId)} />
                                        </TableCell>
                                        <TableCell>{fmtDay(r.day)}</TableCell>
                                        <TableCell>
                                          {fmtTime(r.time)}
                                          {r.hidden ? (
                                            <VisibilityOff titleAccess="Not shown on the public schedule" sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: 'text.disabled' }} />
                                          ) : null}
                                        </TableCell>
                                        {multiClass ? <TableCell>{r.className}</TableCell> : null}
                                        <TableCell>{r.booked}/{r.slots}</TableCell>
                                        <TableCell>
                                          {can ? (
                                            <Chip size="small" color={unticked.has(r.sessionId) ? 'default' : 'success'} variant="outlined" label={unticked.has(r.sessionId) ? 'Skipped' : 'Will book'} />
                                          ) : (
                                            <Chip size="small" color={r.status === 'already_booked' ? 'info' : 'default'} variant="outlined" label={r.reason ?? r.status} />
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      <FormControlLabel
                        control={<Checkbox size="small" checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
                        label={<Typography variant="body2">Send the member one notification for all the bookings</Typography>}
                      />
                    </Box>
                  ) : null}
                </Box>
              </>
            )}
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {tab === 'cancel' ? (
          <Button onClick={onClose}>Close</Button>
        ) : result ? (
          <Button variant="contained" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button onClick={close} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={!toBook.length || submitting || previewing}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <EventRepeat />}
            >
              {submitting ? 'Booking…' : toBook.length ? `Book ${plural(toBook.length, 'class', 'classes')}` : 'Book classes'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkPackBookingDialog;
